/**
 * PlanCropperDialog — interactive cropper for normalized plan images.
 *
 * Wraps `react-easy-crop` with:
 *   - pan / zoom / rotation controls
 *   - aspect-ratio presets (free, 16:9, 4:3, 1:1, A4 paysage/portrait)
 *   - rasterization via <canvas> at native pixel resolution
 *   - returns a `Blob` (image/png) + dimensions to the caller
 *
 * Self-contained: no API calls. The parent decides what to do with the blob
 * (upload to backend, store as normalized_plan_image_url, etc.).
 *
 * Props:
 *   open: bool
 *   imageUrl: string          source image (PNG/JPG)
 *   initialCrop?: object      { x, y, width, height, rotation, zoom } — résume une session précédente
 *   title?: string            entête du dialog
 *   onCancel: () => void
 *   onApply: ({ blob, dataUrl, croppedAreaPixels, rotation, zoom }) => Promise<void> | void
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import {
  X, RotateCw, RotateCcw, ZoomIn, ZoomOut, RefreshCcw, Check, Crop as CropIcon,
  Loader2, Square, Maximize2, Palette, Sparkles,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';

const ASPECT_PRESETS = [
  { id: 'free', label: 'Libre', value: undefined, icon: Maximize2 },
  { id: 'a4l', label: 'A4 paysage', value: 297 / 210, icon: Square },
  { id: 'a4p', label: 'A4 portrait', value: 210 / 297, icon: Square },
  { id: '16_9', label: '16:9', value: 16 / 9, icon: Square },
  { id: '4_3', label: '4:3', value: 4 / 3, icon: Square },
  { id: '1_1', label: 'Carré', value: 1, icon: Square },
];

// Loads an HTMLImageElement from a URL (with CORS) — used during rasterization
const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'use-credentials';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });

/**
 * Rasterize the cropped area to a PNG blob at the source image's native
 * resolution. Applies rotation around the image center, then post-filters
 * (grayscale + contrast boost) via canvas pixel manipulation.
 */
async function rasterizeCrop(imageUrl, croppedAreaPixels, rotationDeg, filters = {}) {
  const img = await loadImage(imageUrl);
  // Step 1: render the rotated image into a "rotated canvas" big enough to hold any rotation
  const rad = (rotationDeg * Math.PI) / 180;
  const safe = Math.max(img.width, img.height) * 2;
  const rotated = document.createElement('canvas');
  rotated.width = safe;
  rotated.height = safe;
  const rctx = rotated.getContext('2d');
  rctx.translate(safe / 2, safe / 2);
  rctx.rotate(rad);
  rctx.drawImage(img, -img.width / 2, -img.height / 2);

  // Step 2: read back what react-easy-crop computed in *natural* image coords
  //         (croppedAreaPixels.x / y are relative to the rotated image, top-left = (safe-iw)/2, (safe-ih)/2)
  const offsetX = (safe - img.width) / 2;
  const offsetY = (safe - img.height) / 2;

  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(croppedAreaPixels.width));
  out.height = Math.max(1, Math.round(croppedAreaPixels.height));
  const octx = out.getContext('2d');
  octx.drawImage(
    rotated,
    croppedAreaPixels.x + offsetX,
    croppedAreaPixels.y + offsetY,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    out.width,
    out.height,
  );

  // Step 3: apply post-filters in pixel space
  applyCanvasFilters(out, filters);

  // Step 4: produce a Blob
  return new Promise((resolve) => {
    out.toBlob(
      (blob) => resolve({ blob, dataUrl: out.toDataURL('image/png'), width: out.width, height: out.height }),
      'image/png',
      1,
    );
  });
}

/**
 * Applique les filtres "Nettoyage Raster" sur un canvas :
 *   - grayscale : Rec 601 luma (Y = 0.299R + 0.587G + 0.114B)
 *   - auto_contrast : étalement min/max sur 99% des pixels (cutoff 1%)
 *   - contrast_boost : multiplicateur de contraste autour de 128
 */
function applyCanvasFilters(canvas, filters) {
  const { grayscale, autoContrast, contrastBoost } = filters || {};
  if (!grayscale && !autoContrast && (!contrastBoost || Math.abs(contrastBoost - 1) < 0.01)) return;
  const ctx = canvas.getContext('2d');
  const w = canvas.width;
  const h = canvas.height;
  const imgData = ctx.getImageData(0, 0, w, h);
  const data = imgData.data;

  if (grayscale) {
    for (let i = 0; i < data.length; i += 4) {
      const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      data[i] = data[i + 1] = data[i + 2] = luma;
    }
  }

  if (autoContrast) {
    // Étalement : on calcule le min et max sur le canal "luma" puis on remap [min..max] → [0..255]
    let lmin = 255;
    let lmax = 0;
    const total = w * h;
    // Sample 5% of pixels for speed
    const step = Math.max(1, Math.floor(total / 20000)) * 4;
    for (let i = 0; i < data.length; i += step) {
      const l = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      if (l < lmin) lmin = l;
      if (l > lmax) lmax = l;
    }
    if (lmax - lmin > 10) {
      const scale = 255 / (lmax - lmin);
      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.max(0, Math.min(255, (data[i] - lmin) * scale));
        data[i + 1] = Math.max(0, Math.min(255, (data[i + 1] - lmin) * scale));
        data[i + 2] = Math.max(0, Math.min(255, (data[i + 2] - lmin) * scale));
      }
    }
  }

  if (contrastBoost && Math.abs(contrastBoost - 1) > 0.01) {
    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.max(0, Math.min(255, (data[i] - 128) * contrastBoost + 128));
      data[i + 1] = Math.max(0, Math.min(255, (data[i + 1] - 128) * contrastBoost + 128));
      data[i + 2] = Math.max(0, Math.min(255, (data[i + 2] - 128) * contrastBoost + 128));
    }
  }

  ctx.putImageData(imgData, 0, 0);
}

const PlanCropperDialog = ({
  open,
  imageUrl,
  initialCrop,
  title = 'Recadrer le plan',
  onCancel,
  onApply,
}) => {
  const [crop, setCrop] = useState(initialCrop?.crop || { x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialCrop?.zoom || 1);
  const [rotation, setRotation] = useState(initialCrop?.rotation || 0);
  const [aspect, setAspect] = useState(initialCrop?.aspect ?? undefined); // undefined = free
  const [aspectPreset, setAspectPreset] = useState(initialCrop?.aspectPreset || 'free');
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewBlobRef = useRef(null);
  // Filtres "Nettoyage Raster" (Iter 63)
  const [grayscale, setGrayscale] = useState(initialCrop?.filters?.grayscale || false);
  const [autoContrast, setAutoContrast] = useState(initialCrop?.filters?.autoContrast || false);
  const [contrastBoost, setContrastBoost] = useState(initialCrop?.filters?.contrastBoost || 1);

  const filters = { grayscale, autoContrast, contrastBoost };

  // Reset state when imageUrl changes (new document loaded)
  useEffect(() => {
    if (!open) return;
    setCrop(initialCrop?.crop || { x: 0, y: 0 });
    setZoom(initialCrop?.zoom || 1);
    setRotation(initialCrop?.rotation || 0);
    setCroppedAreaPixels(null);
    setPreviewUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, open]);

  // Live preview: every time the crop area OR filters change, regenerate a thumb after 200ms
  useEffect(() => {
    if (!croppedAreaPixels || !imageUrl) return;
    const t = setTimeout(async () => {
      try {
        const r = await rasterizeCrop(imageUrl, croppedAreaPixels, rotation, filters);
        if (previewBlobRef.current) URL.revokeObjectURL(previewBlobRef.current);
        const url = URL.createObjectURL(r.blob);
        previewBlobRef.current = url;
        setPreviewUrl(url);
      } catch {/* ignore */}
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [croppedAreaPixels, rotation, imageUrl, grayscale, autoContrast, contrastBoost]);

  useEffect(() => {
    return () => {
      if (previewBlobRef.current) URL.revokeObjectURL(previewBlobRef.current);
    };
  }, []);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setGrayscale(false);
    setAutoContrast(false);
    setContrastBoost(1);
  };

  const handleApply = async () => {
    if (!croppedAreaPixels || !imageUrl) return;
    setBusy(true);
    try {
      const r = await rasterizeCrop(imageUrl, croppedAreaPixels, rotation, filters);
      await onApply?.({
        blob: r.blob,
        dataUrl: r.dataUrl,
        width: r.width,
        height: r.height,
        croppedAreaPixels,
        rotation,
        zoom,
        aspectPreset,
        filters,
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1500]" data-testid="plan-cropper-dialog">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(8,8,10,0.86)', backdropFilter: 'blur(4px)' }}
        onClick={busy ? undefined : onCancel}
      />

      {/* Modal */}
      <div
        className="relative w-[96vw] h-[92vh] max-w-[1480px] mx-auto mt-[4vh] rounded-2xl flex flex-col overflow-hidden"
        style={{
          background: 'linear-gradient(to bottom, #0F0F12, #09090B)',
          border: '1px solid var(--c-border, #2a2a2e)',
          boxShadow: 'var(--shadow-cockpit, 0 32px 80px rgba(0,0,0,0.7))',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'var(--c-border, #2a2a2e)' }}>
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: 'var(--c-gold-soft, rgba(217,160,91,0.12))', border: '1px solid var(--c-border-accent, rgba(217,160,91,0.35))' }}
            >
              <CropIcon className="w-4 h-4" style={{ color: 'var(--c-gold, #D9A05B)' }} />
            </div>
            <div>
              <span className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: 'var(--c-gold, #D9A05B)' }}>
                Asset Pipeline · Recadrage
              </span>
              <h2 className="text-[17px] font-semibold tracking-tight" style={{ color: 'var(--c-text, #E9E9EE)', fontFamily: 'Outfit, sans-serif' }}>
                {title}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/5 transition-colors"
            data-testid="plan-cropper-close"
          >
            <X className="w-4 h-4 text-white/60" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* Cropper canvas */}
          <div className="relative bg-[#050507]" data-testid="plan-cropper-canvas">
            {imageUrl ? (
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                showGrid
                restrictPosition={false}
                objectFit="contain"
                style={{
                  containerStyle: { background: '#050507' },
                  cropAreaStyle: {
                    border: '1.5px solid #D9A05B',
                    color: 'rgba(217,160,91,0.18)',
                  },
                }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm">
                Aucune image source
              </div>
            )}
          </div>

          {/* Side panel: controls + preview */}
          <aside className="border-l flex flex-col" style={{ borderColor: 'var(--c-border, #2a2a2e)' }}>
            {/* Preview */}
            <div className="p-4 border-b" style={{ borderColor: 'var(--c-border, #2a2a2e)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: 'var(--c-text-muted, rgba(255,255,255,0.55))' }}>
                  Aperçu sortie
                </span>
                {croppedAreaPixels && (
                  <span className="text-[10.5px] font-mono" style={{ color: 'rgba(255,255,255,0.5)' }} data-testid="plan-cropper-dim">
                    {Math.round(croppedAreaPixels.width)}×{Math.round(croppedAreaPixels.height)}
                  </span>
                )}
              </div>
              <div
                className="aspect-[4/3] rounded-lg overflow-hidden flex items-center justify-center"
                style={{ background: '#0a0a0d', border: '1px solid var(--c-border, #2a2a2e)' }}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="Aperçu" className="max-w-full max-h-full object-contain" data-testid="plan-cropper-preview" />
                ) : (
                  <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                    Ajuste la zone pour voir l'aperçu
                  </span>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Aspect ratio */}
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase font-semibold block mb-1.5" style={{ color: 'var(--c-text-muted, rgba(255,255,255,0.55))' }}>
                  Format
                </label>
                <div className="grid grid-cols-3 gap-1.5">
                  {ASPECT_PRESETS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { setAspectPreset(p.id); setAspect(p.value); }}
                      className="px-2 py-1.5 rounded-md text-[11px] font-medium transition-colors"
                      style={{
                        background: aspectPreset === p.id ? 'var(--c-gold-soft, rgba(217,160,91,0.18))' : 'rgba(255,255,255,0.04)',
                        color: aspectPreset === p.id ? 'var(--c-gold, #D9A05B)' : 'rgba(255,255,255,0.7)',
                        border: `1px solid ${aspectPreset === p.id ? 'var(--c-border-accent, rgba(217,160,91,0.35))' : 'rgba(255,255,255,0.08)'}`,
                      }}
                      data-testid={`plan-cropper-aspect-${p.id}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Zoom */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: 'var(--c-text-muted, rgba(255,255,255,0.55))' }}>
                    Zoom
                  </label>
                  <span className="text-[10.5px] font-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>{zoom.toFixed(2)}×</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}
                    className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-white/5"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                    data-testid="plan-cropper-zoom-out"
                  >
                    <ZoomOut className="w-3.5 h-3.5 text-white/70" />
                  </button>
                  <Slider
                    value={[zoom]}
                    onValueChange={(v) => setZoom(v[0])}
                    min={0.5}
                    max={4}
                    step={0.05}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.min(4, z + 0.1))}
                    className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-white/5"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                    data-testid="plan-cropper-zoom-in"
                  >
                    <ZoomIn className="w-3.5 h-3.5 text-white/70" />
                  </button>
                </div>
              </div>

              {/* Rotation */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: 'var(--c-text-muted, rgba(255,255,255,0.55))' }}>
                    Rotation
                  </label>
                  <span className="text-[10.5px] font-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>{rotation}°</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setRotation((r) => r - 90)}
                    className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-white/5"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                    title="-90°"
                    data-testid="plan-cropper-rotate-left"
                  >
                    <RotateCcw className="w-3.5 h-3.5 text-white/70" />
                  </button>
                  <Slider
                    value={[rotation]}
                    onValueChange={(v) => setRotation(v[0])}
                    min={-180}
                    max={180}
                    step={1}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => setRotation((r) => r + 90)}
                    className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-white/5"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                    title="+90°"
                    data-testid="plan-cropper-rotate-right"
                  >
                    <RotateCw className="w-3.5 h-3.5 text-white/70" />
                  </button>
                </div>
              </div>

              {/* Filtres "Nettoyage Raster" (Iter 63) */}
              <div className="pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <label className="text-[10px] tracking-[0.2em] uppercase font-semibold flex items-center gap-1.5 block mb-2" style={{ color: 'var(--c-gold, #D9A05B)' }}>
                  <Palette className="w-3 h-3" />
                  Nettoyage Raster
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="plan-cropper-filter-grayscale-label">
                    <input
                      type="checkbox"
                      checked={grayscale}
                      onChange={(e) => setGrayscale(e.target.checked)}
                      className="accent-amber-600"
                      data-testid="plan-cropper-filter-grayscale"
                    />
                    <span className="text-[12px]" style={{ color: 'rgba(255,255,255,0.85)' }}>Noir & Blanc</span>
                    <span className="text-[10.5px] ml-auto" style={{ color: 'rgba(255,255,255,0.4)' }}>luma 601</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="plan-cropper-filter-autocontrast-label">
                    <input
                      type="checkbox"
                      checked={autoContrast}
                      onChange={(e) => setAutoContrast(e.target.checked)}
                      className="accent-amber-600"
                      data-testid="plan-cropper-filter-autocontrast"
                    />
                    <span className="text-[12px]" style={{ color: 'rgba(255,255,255,0.85)' }}>Contraste auto</span>
                    <span className="text-[10.5px] ml-auto inline-flex items-center gap-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      <Sparkles className="w-2.5 h-2.5" />
                      étalement
                    </span>
                  </label>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.7)' }}>Contraste manuel</span>
                      <span className="text-[10.5px] font-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>{contrastBoost.toFixed(2)}×</span>
                    </div>
                    <Slider
                      value={[contrastBoost]}
                      onValueChange={(v) => setContrastBoost(v[0])}
                      min={0.5}
                      max={2.5}
                      step={0.05}
                    />
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleReset}
                className="w-full px-3 py-2 rounded-md text-[12px] font-medium transition-colors flex items-center justify-center gap-1.5"
                style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.08)' }}
                data-testid="plan-cropper-reset"
              >
                <RefreshCcw className="w-3.5 h-3.5" />
                Réinitialiser
              </button>
            </div>
          </aside>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex items-center justify-between" style={{ borderColor: 'var(--c-border, #2a2a2e)' }}>
          <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Astuce : maintiens <kbd className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)' }}>clic gauche</kbd> pour déplacer, <kbd className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)' }}>molette</kbd> pour zoomer.
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onCancel} disabled={busy} data-testid="plan-cropper-cancel">
              Annuler
            </Button>
            <Button
              onClick={handleApply}
              disabled={busy || !croppedAreaPixels}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="plan-cropper-apply"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Application…
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Valider le recadrage
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanCropperDialog;
